// /police-clearance/$type — Type detail (TH)
import QuotableStatsStrip from "@/components/QuotableStatsStrip";
import { createFileRoute, notFound, Link } from "@tanstack/react-router";
import { POLICE_CLEARANCE_TYPES, getPoliceType } from "@/data/police-clearance-types";
import { policeTypeSectionsTh } from "@/data/police-content";
import { PoliceSections } from "@/components/PoliceSections";
import { consolidateGraph } from "@/lib/schema-graph";
import { aggregateRatingNode, reviewNodes } from "@/lib/programmatic-ratings";
import { speakableSchema } from "@/lib/aeo";
import { hreflangLinks, ctaDesc, ctaTitle } from "@/lib/seo";
import { ContactCTA, FaqAccordion } from "@/components/seo/Blocks";
import { TrustLayer } from "@/components/TrustLayer";
import { locations } from "@/data/locations";
import { ShieldCheck, MessageCircle, Phone, MapPin, BadgeCheck } from "lucide-react";

const SITE = "https://nyclanguageinstitute.com";

// Curated top combo locations (Bangkok hubs + key provinces + branches)
export const POLICE_COMBO_LOC_SLUGS = [
  // Branches
  "bangkok-hq", "khonkaen", "udonthani", "nongkhai",
  // Top BKK districts (CBD + key residential)
  "bkk-pathum-wan", "bkk-bang-rak", "bkk-sathon", "bkk-watthana", "bkk-khlong-toei",
  "bkk-huai-khwang", "bkk-chatuchak", "bkk-phaya-thai", "bkk-ratchathewi",
  "bkk-din-daeng", "bkk-bang-na", "bkk-suan-luang", "bkk-phra-khanong",
  "bkk-lat-phrao", "bkk-bang-kapi", "bkk-thon-buri", "bkk-bangkok-yai",
  // Top provinces
  "province-chiang-mai", "province-chiang-rai", "province-phuket", "province-chon-buri",
  "province-rayong", "province-nonthaburi", "province-pathum-thani", "province-samut-prakan",
  "province-nakhon-ratchasima", "province-khon-kaen", "province-udon-thani",
  "province-songkhla", "province-surat-thani", "province-prachuap-khiri-khan",
] as const;

export const Route = createFileRoute("/police-clearance/$type/")({
  loader: ({ params }) => {
    if (!getPoliceType(params.type)) throw notFound();
    return { slug: params.type };
  },
  head: ({ params }) => {
    const t = getPoliceType(params.type);
    if (!t) return { meta: [{ title: ctaTitle("ไม่พบบริการ", "/police-clearance/$type", "th")}] };
    const url = `${SITE}/police-clearance/${t.slug}`;
    const title = `${t.nameTh} เริ่ม ฿${t.priceFromTh.toLocaleString()} เสร็จ ${t.turnaround} | NYC Police Clearance`;
    const desc = `${t.oneLineTh} · ออกโดย ${t.agency} · ${t.legalizationPath} — ส่งภาพเอกสารให้เราตรวจก่อน ฟรี`;
    const faqs = [
      { q: `${t.nameTh} ราคาเท่าไหร่?`, a: `เริ่ม ฿${t.priceFromTh.toLocaleString()} เสร็จ ${t.turnaround} โดย NYC จัดการให้ครบตั้งแต่ขอเอกสาร แปล MFA/Apostille จนถึงจัดส่งคืน` },
      { q: `${t.nameTh} ใช้ที่ประเทศไหนได้?`, a: `ใช้ได้กับ ${t.acceptedBy.join(", ")} หลังผ่าน Legalization` },
      { q: `เอกสารที่ต้องเตรียมมีอะไรบ้าง?`, a: t.required.join(" · ") },
      { q: `ขั้นตอน Legalization คืออะไร?`, a: t.legalizationPath },
    ];
    return {
      meta: [
        { title: ctaTitle(title, "/police-clearance/$type", "th") },
        { name: "description", content: ctaDesc(desc, "/police-clearance/$type", "th")},
        { name: "keywords", content: `${t.nameTh}, ${t.nameEn}, Police Clearance, ตรวจประวัติอาชญากรรม, ${t.agency}` },
        { property: "og:title", content: ctaTitle(title, "/police-clearance/$type", "th")},
        { property: "og:description", content: ctaDesc(desc, "/police-clearance/$type", "th")},
        { property: "og:url", content: url },
        { property: "og:locale", content: "th_TH" },
      ],
      links: [
        { rel: "canonical", href: url },
        ...hreflangLinks(`/police-clearance/${t.slug}`),
      ],
      scripts: [
        consolidateGraph([
          speakableSchema(),
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "Service",
              "@id": `${url}#service`,
              name: t.nameEn,
              alternateName: t.nameTh,
              serviceType: "Police Clearance",
              areaServed: { "@type": "Country", name: "Thailand" },
              provider: { "@type": "LegalService", "@id": `${SITE}/#org`, name: "NYC Legal & Notary Service", telephone: "+66832494999", url: SITE },
              offers: { "@type": "Offer", price: t.priceFromTh, priceCurrency: "THB", availability: "https://schema.org/InStock" },
              aggregateRating: aggregateRatingNode(url),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "@id": `${url}#faq`,
              mainEntity: faqs.map((f) => ({
                "@type": "Question",
                name: f.q,
                acceptedAnswer: { "@type": "Answer", text: f.a },
              })),
            }),
          },
          {
            type: "application/ld+json" as const,
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "@id": `${url}#breadcrumb`,
              itemListElement: [
                { "@type": "ListItem", position: 1, name: "Home", item: SITE },
                { "@type": "ListItem", position: 2, name: "Police Clearance", item: `${SITE}/police-clearance` },
                { "@type": "ListItem", position: 3, name: t.nameTh, item: url },
              ],
            }),
          },
          ...reviewNodes(url, `${url}#service`, 3).map((r) => ({
            type: "application/ld+json" as const,
            children: JSON.stringify(r),
          })),
        ]),
      ],
    };
  },
  component: PoliceTypePage,
  notFoundComponent: () => (
    <main className="min-h-screen bg-navy-deep p-12 text-white">
      <h1 className="text-2xl font-bold">ไม่พบบริการ</h1>
      <Link to="/police-clearance" className="mt-4 inline-block text-gold underline">กลับ Police Clearance hub</Link>
          <QuotableStatsStrip cluster="police" locale="th" variant="light" />
    </main>
  ),
});

function PoliceTypePage() {
  const { slug } = Route.useLoaderData() as { slug: string };
  const t = getPoliceType(slug)!;
  const sections = policeTypeSectionsTh(t);
  const related = POLICE_CLEARANCE_TYPES.filter((x) => x.slug !== slug).slice(0, 6);
  const comboLocs = POLICE_COMBO_LOC_SLUGS
    .map((s) => locations.find((l) => l.slug === s))
    .filter((l): l is NonNullable<typeof l> => !!l)
    .slice(0, 18);

  return (
    <main className="min-h-screen bg-white">
      <section className="relative overflow-hidden bg-navy-deep text-white">
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_25%_0%,hsl(var(--gold)/0.18),transparent_55%)]" aria-hidden />
        <div className="relative mx-auto max-w-5xl px-4 py-14 sm:px-6 sm:py-20">
          <nav aria-label="Breadcrumb" className="mb-5 text-[12px] text-white/80">
            <Link to="/" className="hover:text-gold">หน้าแรก</Link>
            <span className="px-1.5 text-white/30">/</span>
            <Link to="/police-clearance" className="hover:text-gold">Police Clearance</Link>
            <span className="px-1.5 text-white/30">/</span>
            <span className="text-white/85">{t.nameTh}</span>
          </nav>
          <div className="inline-flex items-center gap-2 rounded-full border border-gold/40 bg-gold/10 px-3 py-1 text-[11px] font-bold uppercase tracking-widest text-gold">
            <ShieldCheck className="h-3.5 w-3.5" /> {t.nameEn}
          </div>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-tight sm:text-5xl">{t.nameTh}</h1>
          <p className="mt-4 max-w-3xl text-base text-white/85 sm:text-lg">{t.oneLineTh}</p>
          <dl className="mt-7 grid grid-cols-2 gap-3 sm:grid-cols-4">
            {[
              { k: "ราคาเริ่ม", v: `฿${t.priceFromTh.toLocaleString()}` },
              { k: "เสร็จภายใน", v: t.turnaround },
              { k: "ออกโดย", v: t.country },
              { k: "Rating", v: "4.9 / 2,147" },
            ].map((s) => (
              <div key={s.k} className="rounded-2xl border border-white/10 bg-white/[0.04] p-3 backdrop-blur">
                <dt className="text-[10px] font-bold uppercase tracking-widest text-gold">{s.k}</dt>
                <dd className="mt-1 text-sm font-extrabold text-white sm:text-base">{s.v}</dd>
              </div>
            ))}
          </dl>
          <div className="mt-7 flex flex-wrap gap-2.5">
            <a href="https://line.me/ti/p/@nycli" target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 px-5 py-2.5 text-sm font-bold text-white shadow-md">
              <MessageCircle className="h-4 w-4" /> ปรึกษา LINE
            </a>
            <a href="tel:0832494999" className="inline-flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-sm font-bold text-navy-deep shadow-md">
              <Phone className="h-4 w-4" /> 083-249-4999
            </a>
          </div>
        </div>
      </section>

      <div className="mx-auto max-w-5xl px-4 py-12 sm:px-6 sm:py-14">
        <blockquote className="aeo-speakable rounded-2xl border-l-4 border-gold bg-amber-50/60 p-5 text-[14px] leading-relaxed text-slate-800 sm:text-[15px]">
          <span className="mr-2 inline-flex items-center gap-1 rounded-full bg-gold/20 px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-amber-800">
            <BadgeCheck className="h-3 w-3" /> Quotable
          </span>
          {t.oneLineTh}
        </blockquote>

        <div className="mt-10">
          <PoliceSections sections={sections} />
        </div>

        <section className="mt-12 rounded-3xl border border-amber-200 bg-gradient-to-br from-amber-50 to-white p-6 sm:p-8">
          <ContactCTA />
        </section>

        <section className="mt-14">
          <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">คำถามที่พบบ่อย</h2>
          <div className="mt-5">
            <FaqAccordion
              items={[
                { q: `${t.nameTh} ราคาเท่าไหร่?`, a: `เริ่ม ฿${t.priceFromTh.toLocaleString()} เสร็จ ${t.turnaround}` },
                { q: `ใช้ที่ประเทศไหนได้?`, a: `${t.acceptedBy.join(", ")}` },
                { q: `เอกสารที่ต้องเตรียม?`, a: t.required.join(" · ") },
                { q: `Legalization ทำอย่างไร?`, a: t.legalizationPath },
              ]}
            />
          </div>
        </section>

        {comboLocs.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">{t.nameTh} ในพื้นที่ใกล้คุณ</h2>
            <p className="mt-2 text-sm text-slate-600">ครอบคลุมทุกย่านธุรกิจ ใกล้ BTS/MRT และห้างชั้นนำทั่วประเทศ</p>
            <div className="mt-5 grid gap-2.5 sm:grid-cols-2 lg:grid-cols-3">
              {comboLocs.map((l) => (
                <Link
                  key={l.slug}
                  to="/police-clearance/$type/in/$location"
                  params={{ type: slug, location: l.slug }}
                  className="group flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3.5 py-2.5 text-sm text-slate-800 transition hover:border-gold/50"
                >
                  <MapPin className="h-4 w-4 shrink-0 text-amber-600" />
                  <span className="font-semibold group-hover:text-amber-700">{t.nameTh} · {l.nameTh}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {related.length > 0 && (
          <section className="mt-14 border-t border-slate-200 pt-10">
            <h2 className="font-display text-2xl font-extrabold text-navy-deep sm:text-3xl">บริการ Police Clearance อื่น</h2>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((r) => (
                <Link key={r.slug} to="/police-clearance/$type" params={{ type: r.slug }} className="rounded-2xl border border-slate-200 bg-white p-4 hover:border-gold/50">
                  <div className="font-display text-sm font-extrabold text-navy-deep">{r.nameTh}</div>
                  <div className="mt-0.5 text-[11px] text-slate-500">เริ่ม ฿{r.priceFromTh.toLocaleString()} · {r.turnaround}</div>
                </Link>
              ))}
            </div>
          </section>
        )}
      </div>

      <TrustLayer variant="compact" />
          <QuotableStatsStrip cluster="police" locale="th" variant="light" />
    </main>
  );
}
